<template>
  <!-- Filters -->
  <div :class="['filters', showFilters ? 'filters--active' : '']">
    <!-- Filters toggle -->
    <button
      class="filters__toggle"
      type="button"
      @click="toggleFilters"
      :aria-busy="isLoading"
      aria-live="polite"
    >
      {{ filterButtonText }}
      <span>Filter</span>
    </button>

    <!-- Filters collapse -->
    <div class="filters__collapse">
      <!-- Clear Filters -->
      <search-clear-filters v-if="isFiltering" link-classes="btn bg-b-blue" />

      <!-- Residence type -->
      <search-checkboxes-filter
        :options.prop="[
          {
            label: 'Undergraduate',
            name: 'undergraduate',
            field: 'Undergraduate',
            value: 'Yes',
            selected: false,
            class: 'data-filter-item',
          },
          {
            label: 'Postgraduate',
            name: 'postgraduate',
            field: 'Postgraduate',
            value: 'Yes',
            selected: false,
            class: 'data-filter-item',
          },
        ]"
      >
        <div class="filter-heading">
          <legend>Residence type</legend>
        </div>
      </search-checkboxes-filter>

      <!-- Room type -->
      <search-checkboxes-filter
        :options.prop="[
          {
            label: 'Single rooms',
            name: 'single-rooms',
            field: 'StandardRooms',
            value: 'Yes',
            selected: false,
            class: 'data-filter-item',
          },
          {
            label: 'En suite rooms',
            name: 'en-suite-rooms',
            field: 'EnsuiteRooms',
            value: 'Yes',
            selected: false,
            class: 'data-filter-item',
          },
          {
            label: 'Twin rooms',
            name: 'twin-rooms',
            field: 'SharedRooms',
            value: 'Yes',
            selected: false,
            class: 'data-filter-item',
          },
        ]"
      >
        <div class="filter-heading">
          <legend aria-describedby="room-type-help">Room type</legend>
          <Popper v-bind="popperConfiguration('room-type-help')">
            <template #reference>
              <span aria-hidden="true">?</span>
              <span class="sr-only">Room type help</span>
            </template>
            <p>
              Rooms are categorised and priced according to the number of
              occupants, bathroom facilities, decor, and layout.
            </p>
            <a
              href="http://www.bristol.ac.uk/accommodation/about/room-types/"
              target="_blank"
              >Find out more.</a
            >
          </Popper>
        </div>
      </search-checkboxes-filter>

      <!-- Catering -->
      <search-checkboxes-filter
        :options.prop="[
          {
            label: 'Self-catered rooms',
            name: 'self-catered',
            field: 'SelfCatered',
            value: 'Yes',
            selected: false,
            class: 'data-filter-item',
          },
          {
            label: 'Catered rooms',
            name: 'catered',
            field: 'Catered',
            value: 'Yes',
            selected: false,
            class: 'data-filter-item',
          },
        ]"
      >
        <div class="filter-heading">
          <legend aria-describedby="catering-help">Catering</legend>
          <Popper v-bind="popperConfiguration('catering-help')">
            <template #reference>
              <span aria-hidden="true">?</span>
              <span class="sr-only">Catering help</span>
            </template>
            <p>
              Our catered accommodation provides two meals a day and
              self-accommodation offers facilities to cook their own meals.
            </p>
            <a
              href="http://www.bristol.ac.uk/accommodation/about/half-board/"
              target="_blank"
              >Find out more.</a
            >
          </Popper>
        </div>
      </search-checkboxes-filter>

      <!-- Tenancy Cost -->
      <search-range-filter
        :labelRenderer.prop="labelRenderer"
        name="total-tenancy-cost"
        field="TotalTenancyCost_number"
        :min="minimumTenancyCost"
        :max="maximumTenancyCost"
      >
        <div class="filter-heading">
          <legend>Tenancy Cost</legend>
        </div>
      </search-range-filter>
    </div>
  </div>
</template>
<script>
export default {
  name: 'SearchFilters',
  props: {
    isFiltering: Boolean,
    isLoading: Boolean,
    resultsCount: Number,
  },
  data() {
    return {
      showFilters: false,
    };
  },
  computed: {
    /*
     * Returns the text for the filter button toggle, determined by loading results
     *
     * @returns {string}
     */
    filterButtonText() {
      return this.isLoading
        ? 'Loading...'
        : `${this.resultsCount} residence${
            this.resultsCount == 1 ? '' : 's'
          } found`;
    },

    /*
     * Returns the minimum value for the tenancy cost range slider
     *
     * @returns {string}
     */
    minimumTenancyCost() {
      // Retrieve minimum tenancy cost meta tag
      const minimumTenancyCost = document.querySelector(
        'meta[name="minimum_tenancy_cost"]'
      );

      // Verify that minimum tenancy cost meta tag exists
      if (minimumTenancyCost) {
        // Return minimum tenancy cost meta tag content
        return minimumTenancyCost.content;
      }

      // There is no minimum tenancy cost meta tag, return default value
      return 0;
    },

    /*
     * Returns the maximum value for the tenancy cost range slider
     *
     * @returns {string}
     */
    maximumTenancyCost() {
      // Retrieve maximum tenancy cost meta tag
      const maximumTenancyCost = document.querySelector(
        'meta[name="maximum_tenancy_cost"]'
      );

      // Verify that maximum tenancy cost meta tag exists
      if (maximumTenancyCost) {
        // Return maximum tenancy cost meta tag content
        return maximumTenancyCost.content;
      }

      // There is no maximum tenancy cost meta tag, return default value
      return 100;
    },
  },
  methods: {
    /*
     * Toggles the visibility of the filters panel
     *
     * @returns {void}
     */
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    /*
     * Overrides the default search range filter label renderer
     *
     * @param {number} value
     * @returns {string}
     */
    labelRenderer(value) {
      return `£${value}`;
    },

    /*
     * Returns the configuration for the Popper tooltip component
     *
     * @params {string} buttonId
     * @returns {object}
     */
    popperConfiguration(buttonId = '') {
      return {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [75, 12],
            },
          },
        ],
        referenceIs: 'button',
        referenceProps: {
          id: buttonId,
          class: 'filter-heading__help',
        },
        popperProps: {
          class: 'filter-heading__tooltip',
        },
        popperIs: 'span',
      };
    },
  },
};
</script>
